<template>
  <figure class="anatomy-map" :style="mapStyle">
    <svg
      class="anatomy-map__canvas"
      :viewBox="`0 0 ${config.canvasWidth} ${canvasHeight}`"
      role="img"
      :aria-label="ariaLabel"
    >
      <title>{{ ariaLabel }}</title>
      <image
        :href="config.image"
        :x="config.imageX"
        y="0"
        :width="config.imageWidth"
        :height="config.imageHeight"
      />

      <g v-for="callout in visibleCallouts" :key="`callout-${callout.index}`" class="anatomy-map__callout">
        <title>{{ `${callout.accessibleTitle}, ${callout.accessibleDetail}` }}</title>
        <polyline
          :points="linePoints(callout)"
          class="anatomy-map__line"
        />
        <circle :cx="callout.anchorX" :cy="callout.anchorY" r="13" class="anatomy-map__halo" />
        <circle :cx="callout.anchorX" :cy="callout.anchorY" r="7" class="anatomy-map__point" />
        <text
          :x="callout.labelX"
          :y="callout.labelY + metrics.titleOffsetY"
          :text-anchor="callout.textAnchor"
          class="anatomy-map__title"
        >
          {{ callout.title }}
        </text>
      </g>

    </svg>
    <figcaption class="sr-only">{{ ariaLabel }}</figcaption>
  </figure>
</template>

<script>
import {
  ANATOMY_CALLOUT_METRICS,
  ANATOMY_MAP_CONFIG,
  buildAnatomyCallouts,
} from '@/utils/anatomyMapLayout'

export default {
  name: 'Report2AnatomyMap',
  props: {
    lesionMap: {
      type: Object,
      required: true,
    },
    isColon: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    kind() {
      return this.isColon ? 'colon' : 'gastro'
    },
    config() {
      return ANATOMY_MAP_CONFIG[this.kind]
    },
    metrics() {
      return ANATOMY_CALLOUT_METRICS
    },
    callouts() {
      return buildAnatomyCallouts(this.lesionMap.groups, this.kind)
    },
    visibleCallouts() {
      return this.callouts.filter((callout) => callout.visible)
    },
    canvasHeight() {
      return this.config.imageHeight
    },
    mapStyle() {
      return {
        maxWidth: `${this.config.canvasWidth}px`,
        '--anatomy-title-size': `${this.metrics.titleFontSize}px`,
        '--anatomy-detail-size': `${this.metrics.detailFontSize}px`,
      }
    },
    ariaLabel() {
      const descriptions = this.callouts.map(
        (item) => `${item.accessibleTitle}, ${item.accessibleDetail}`
      )
      return descriptions.length ? `Lesion anatomy map. ${descriptions.join('. ')}` : 'Lesion anatomy map. No lesion metadata.'
    },
  },
  methods: {
    linePoints(callout) {
      return `${callout.anchorX},${callout.anchorY} ${callout.elbowX},${callout.labelY} ${callout.lineEndX},${callout.labelY}`
    },
  },
}
</script>

<style lang="scss" scoped>
.anatomy-map {
  width: 100%;
  margin: 0 auto;
}

.anatomy-map__canvas {
  display: block;
  width: 100%;
  height: auto;
  overflow: visible;
}

.anatomy-map__line {
  fill: none;
  stroke: #0d9488;
  stroke-width: 2.5;
  vector-effect: non-scaling-stroke;
}

.anatomy-map__halo {
  fill: rgba(13, 148, 136, 0.25);
}

.anatomy-map__point {
  fill: #0d9488;
}

.anatomy-map__title,
.anatomy-map__detail {
  font-family: Arial, sans-serif;
  paint-order: stroke fill;
  stroke: rgba(255, 255, 255, 0.92);
  stroke-width: 2px;
  stroke-linejoin: round;
}

.anatomy-map__title {
  font-size: var(--anatomy-title-size);
  font-weight: 700;
  fill: #15323b;
}

.anatomy-map__detail {
  font-size: var(--anatomy-detail-size);
  fill: #52656d;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media print {
  .anatomy-map__line,
  .anatomy-map__halo,
  .anatomy-map__point {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
